import { ForwardedRef, forwardRef, memo, useMemo } from 'react';
import { Pressable, Dimensions, Platform } from 'react-native';
import { RegistrationFieldProps } from '../../RegistrationField.types';
import { useSelector } from '@betty/toolkit/state';
import { selectRegistrationOffers } from '../../../../state';
import {
  Box,
  Breakpoint,
  Icon,
  Image,
  Text,
  TextInput,
  useBreakpoint,
} from '@betty/moonlight';
import RewardBadge from '../../../RewardBadge/RewardBadge';

function OfferField(
  { value, onValue, error }: RegistrationFieldProps<'promoId'>,
  _: ForwardedRef<TextInput>,
) {
  const registrationOffers = useSelector(selectRegistrationOffers);
  const breakpoint = useBreakpoint();
  const offers = useMemo(() => registrationOffers || [], [registrationOffers]);
  const offer = useMemo(
    () => offers.find(({ promotionId }) => promotionId === value),
    [value, offers],
  );

  const isMobile = breakpoint === Breakpoint.MOBILE;

  const rewards = useMemo(() => offer?.rewards || [], [offer?.rewards]);

  const offerItemStylesMobile = useMemo(() => {
    const itemsPerRow = 3;
    const containerWidth = 100;
    const itemWidth = 28;
    const gap = 20;
    const screenWidth = Dimensions.get('window').width;
    const horizontalGapPercent = (gap / screenWidth) * 100;
    const totalItemsWidth = itemWidth * itemsPerRow;
    const totalHorizontalGap = horizontalGapPercent * (itemsPerRow - 1);
    const remainingSpace =
      containerWidth - totalItemsWidth - totalHorizontalGap;
    const sideMargin = remainingSpace / 2;

    return offers.map((_, index) => {
      const isFirstInRow = index % itemsPerRow === 0;
      const isLastInRow = (index + 1) % itemsPerRow === 0;

      return {
        width: `${itemWidth}%`,
        marginLeft: isFirstInRow
          ? `${sideMargin}%`
          : `${horizontalGapPercent}%`,
        marginRight: isLastInRow ? `${sideMargin}%` : '0%',
        marginBottom: gap,
      };
    });
  }, [offers]);

  const offerItemStylesDesktop = useMemo(() => {
    const itemsPerRow = 3;
    const itemWidth = 100 / itemsPerRow;
    const gap = 20;
    const screenWidth = Dimensions.get('window').width;
    const horizontalGapPercent = (gap / screenWidth) * 100;

    return offers.map((_, index) => {
      const isFirstInRow = index % itemsPerRow === 0;
      const isLastInRow = (index + 1) % itemsPerRow === 0;

      return {
        width: `${itemWidth}%`,
        paddingLeft: isFirstInRow ? '0%' : `${horizontalGapPercent / 2}%`,
        paddingRight: isLastInRow ? '0%' : `${horizontalGapPercent / 2}%`,
        marginBottom: gap,
      };
    });
  }, [offers]);

  return (
    <Box
      marginBottom={Platform.select({
        ios: 'space-0',
        web: 'space-200',
        android: 'space-0',
      })}
    >
      <Box
        marginLeft={{
          desktop: 'space-0',
          tablet: 'space-0',
          mobile: 'space-400',
        }}
      >
        <Text
          variant="h4"
          color="darkSurfacePrimary"
          locNS="registration"
          locKey="offerLabel"
          marginBottom="space-300"
        />
      </Box>
      <Box
        marginLeft={{
          desktop: 'space-0',
          tablet: 'space-0',
          mobile: 'space-400',
        }}
      >
        {offer ? <RewardBadge rewards={rewards} /> : null}
      </Box>
      <Box flexDirection="row" flexWrap="wrap" justifyContent="flex-start">
        {offers.map((offer, index) => {
          return (
            <Pressable
              key={offer?.promotionId}
              onPress={() => onValue(offer?.promotionId)}
              style={
                isMobile
                  ? offerItemStylesMobile[index]
                  : offerItemStylesDesktop[index]
              }
            >
              <Box
                position="relative"
                maxHeight={{
                  mobile: 105,
                  tablet: 245,
                  desktop: 245,
                }}
                maxWidth={{
                  mobile: 105,
                  tablet: 245,
                  desktop: 245,
                }}
                aspectRatio={Platform.select({
                  ios: undefined,
                  android: undefined,
                  web: 1,
                })}
                transform={
                  offer?.promotionId === value ? 'scale(0.9)' : 'scale(1)'
                }
              >
                <Image
                  height="100%"
                  width="100%"
                  contentFit="cover"
                  borderRadius="radius-300"
                  source={offer?.miniImageUrl || ''}
                />
                {offer?.promotionId === value && (
                  <Box
                    variant="gradient"
                    position="absolute"
                    top="space-0"
                    right="space-0"
                    transform={'translate(50%, -50%)'}
                    gradient="darkSurfaceStatusEmerald"
                    borderRadius="radius-full"
                  >
                    <Icon gradient="accentBackground" name="check" />
                  </Box>
                )}
              </Box>
            </Pressable>
          );
        })}
      </Box>
      {error ? (
        <Box
          borderTopWidth={{
            mobile: 'border-width-0',
            tablet: 'border-width-025',
            desktop: 'border-width-025',
          }}
          borderColor="borderError"
          paddingTop="space-200"
          marginBottom={{
            mobile: 'space-0',
            tablet: 'space-0',
            desktop: 'space-200',
          }}
        >
          <Text color="darkSurfaceError" variant="body-m">
            {error}
          </Text>
        </Box>
      ) : null}
    </Box>
  );
}
export default memo(forwardRef(OfferField));
